import { UnsuccessfulFetchError } from "../../../clients/common/errors";

import type { CreateUIAnalyticsEvent } from "@atlaskit/analytics-next";

import type { TeamType } from "./paid-jira-ai-ffl";

export const PAID_JIRA_AI_FLOW = "paidLandingJiraAi" as const;
export const PAID_JIRA_AI_EXPERIMENT_KEY =
    "cfind6215FrictionlessFirstLookJiraV2" as const;

export type PaidJiraAiPromptType = "template" | "custom" | "mixed";
export type PaidJiraAiTeamTypeCategory = "tech" | "biz" | "unknown";

export type PaidJiraAiStep =
    | "PromptViewed"
    | "PromptEngaged"
    | "PromptTeamTypeSelected"
    | "PromptShuffleClicked"
    | "PromptFieldChanged"
    | "BuildWithAIClicked"
    | "PromptAbandoned"
    | "RecaptchaTokenSucceeded"
    | "RecaptchaTokenFailed"
    | "ContentGenStarted"
    | "ContentGenSucceeded"
    | "ContentGenFailed"
    | "PreviewModalOpened"
    | "PreviewRevealStarted"
    | "PreviewReady"
    | "PreviewModalClosed"
    | "PreviewRetryClicked"
    | "AccountCreationViewed"
    | "AccountCreationSubmitClicked"
    | "OtpRedirected";

export type PaidJiraAiChangedField =
    | "teamType"
    | "workingOn"
    | "goal"
    | "shuffle";

export type PaidJiraAiCommonAttributes = {
    flow: typeof PAID_JIRA_AI_FLOW;
    experimentKey: typeof PAID_JIRA_AI_EXPERIMENT_KEY;
    teamType?: TeamType;
    teamTypeCategory?: PaidJiraAiTeamTypeCategory;
    promptAttemptIndex?: number;
    isRetry?: boolean;
    promptType?: PaidJiraAiPromptType;
    didChangeAnything?: boolean;
    changedFields?: PaidJiraAiChangedField[];
    workingOnLength?: number;
    goalLength?: number;
    cgsKey?: string;
};

export function categorizeTeamType(
    teamType?: TeamType
): PaidJiraAiTeamTypeCategory {
    // Keep this stable and explicit so analysis is consistent.
    switch (teamType) {
        case "software-development":
        case "it-support":
            return "tech";
        case "product-management":
        case "marketing":
        case "project-management":
        case "design":
            return "biz";
        default:
            return "unknown";
    }
}

export function getCgsKeyFromLocationHref(href: string): string | undefined {
    try {
        const url = new URL(href);
        return url.searchParams.get("cgsKey") ?? undefined;
    } catch {
        return undefined;
    }
}

export function derivePromptType(args: {
    workingOnValue: string;
    goalValue: string;
    workingOnSuggestion: string;
    goalSuggestion: string;
}): PaidJiraAiPromptType {
    const workingOnIsCustom = Boolean(args.workingOnValue?.trim());
    const goalIsCustom = Boolean(args.goalValue?.trim());

    if (workingOnIsCustom && goalIsCustom) {
        return "custom";
    }
    if (!workingOnIsCustom && !goalIsCustom) {
        return "template";
    }
    return "mixed";
}

export function firePaidJiraAiEvent(
    createAnalyticsEvent: CreateUIAnalyticsEvent,
    payload: {
        step: PaidJiraAiStep;
        action:
            | "viewed"
            | "started"
            | "clicked"
            | "succeeded"
            | "failed"
            | "closed";
        attributes: PaidJiraAiCommonAttributes & Record<string, unknown>;
        eventType?: "track" | "ui" | "operational";
    }
) {
    createAnalyticsEvent({
        eventType: payload.eventType ?? "track",
        action: payload.action,
        actionSubject: "paidLandingJiraAiExperiment",
        attributes: {
            ...payload.attributes,
            step: payload.step
        }
    }).fire();
}

export function safeErrorContext(error: unknown): {
    errorType: "recaptcha_missing" | "http_error" | "unknown";
    errorName?: string;
    status?: number;
    message?: string;
} {
    if (error instanceof Error) {
        const msg = error.message || "";
        if (msg.includes("Missing reCAPTCHA")) {
            return {
                errorType: "recaptcha_missing",
                errorName: error.name,
                message: msg
            };
        }
        return {
            errorType: "unknown",
            errorName: error.name,
            message: msg,
            status: (error as UnsuccessfulFetchError)?.response?.status
        };
    }

    // Support UnsuccessfulFetchError-like shapes without importing cross-package types.
    if (error && typeof error === "object") {
        const maybeStatus = (error as { status?: unknown }).status;
        if (typeof maybeStatus === "number") {
            return {
                errorType: "http_error",
                status: maybeStatus
            };
        }
    }

    return { errorType: "unknown" };
}
